import { Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { LlmModel } from "@/api/types";
import { BarList } from "@/components/charts";
import { SectionCard } from "@/components/ui/section-card";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LlmFormDialog } from "@/features/llms/components/LlmFormDialog";
import {
  useDeactivateLlmModel,
  useLlmModels,
} from "@/features/llms/hooks/useLlmModels";
import { useLlmCatalogCosts } from "@/features/observability/hooks/useObservability";
import { formatCostUsd } from "@/lib/cost";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

const PROVIDER_COLORS: Record<string, string> = {
  anthropic: "bg-orange-100 text-orange-900",
  openai: "bg-emerald-100 text-emerald-900",
  google: "bg-blue-100 text-blue-900",
  custom: "bg-gray-100 text-gray-900",
};

export function LlmCatalogPage() {
  const { t } = useTranslation("common");
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<LlmModel | null>(null);

  const { data, isLoading, isError, error, refetch } = useLlmModels({
    include_inactive: showInactive,
  });
  const deactivate = useDeactivateLlmModel();
  const canCreate = useCan(Action.LLM_MODEL_CREATED);

  // Últimos 30 dias para o chart de custo por modelo. Endpoint é
  // admin-only no backend; assumimos que o user que chega aqui é admin
  // (a sidebar já filtra). O card segue o toggle "Mostrar inativos":
  // modelo recadastrado (registro antigo inativado) não aparece em
  // dobro a menos que o admin peça para ver os inativos.
  const costFilters = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      from: from.toISOString(),
      to: to.toISOString(),
      include_inactive: showInactive,
    };
  }, [showInactive]);
  const costs = useLlmCatalogCosts(costFilters);
  const costChartData = useMemo(
    () =>
      (costs.data ?? [])
        .filter((m) => m.cost > 0)
        .map((m) => ({ label: m.display_name, value: m.cost })),
    [costs.data],
  );
  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  async function handleDeactivate(model: LlmModel) {
    try {
      await deactivate.mutateAsync(model.id);
      toast.success(t("pages.llmModels.toasts.deactivated"));
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("pages.llmModels.title")}
        description={t("pages.llmModels.description")}
        actions={
          <>
            <Button
              variant={showInactive ? "secondary" : "outline"}
              onClick={() => setShowInactive((v) => !v)}
            >
              {showInactive
                ? t("pages.llmModels.hideInactive")
                : t("pages.llmModels.showInactive")}
            </Button>
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              disabled={!canCreate}
            >
              <Plus className="h-4 w-4" />
              {t("pages.llmModels.new")}
            </Button>
          </>
        }
      />

      {canCreate ? (
        <SectionCard
          title={t("pages.llmModels.charts.costByModel")}
          description={t("pages.llmModels.charts.costByModelHint")}
        >
          {costs.isLoading ? (
            <div className="h-40 animate-pulse rounded-xl bg-ink-100/40" />
          ) : costChartData.length === 0 ? (
            <p className="py-4 text-center text-sm text-ink-500">
              {t("pages.llmModels.charts.noUsage")}
            </p>
          ) : (
            <BarList data={costChartData} valueFormatter={formatCostUsd} />
          )}
        </SectionCard>
      ) : null}

      {data && data.length === 0 ? (
        <EmptyState
          title={t("pages.llmModels.empty")}
          description={t("pages.llmModels.emptyHint")}
        />
      ) : (
        <div className="shadow-card overflow-x-auto rounded-2xl border border-line bg-surface">
          <table className="w-full text-sm">
            <thead className="bg-canvas text-[10px] uppercase tracking-wide text-ink-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">
                  {t("pages.llmModels.columns.provider")}
                </th>
                <th className="px-4 py-3 text-left font-semibold">
                  {t("pages.llmModels.columns.modelId")}
                </th>
                <th className="px-4 py-3 text-left font-semibold">
                  {t("pages.llmModels.columns.name")}
                </th>
                <th className="px-4 py-3 text-left font-semibold">
                  {t("pages.llmModels.columns.status")}
                </th>
                <th className="px-4 py-3 text-left font-semibold">
                  {t("pages.llmModels.columns.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line text-ink-900">
              {data?.map((m) => (
                <tr key={m.id} className="transition-colors hover:bg-canvas/60">
                  <td className="px-4 py-2.5">
                    <Badge
                      variant="outline"
                      className={PROVIDER_COLORS[m.provider] ?? ""}
                    >
                      {m.provider}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-ink-700">
                    {m.model_id}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span>{m.display_name}</span>
                      {m.supports_multimodal ? (
                        <Badge
                          variant="outline"
                          className="border-brand-300 bg-brand-50 text-brand-700"
                        >
                          {t("pages.llmModels.badges.multimodal")}
                        </Badge>
                      ) : null}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    {m.is_active ? (
                      <Badge variant="success">
                        {t("pages.llmModels.badges.active")}
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        {t("pages.llmModels.badges.inactive")}
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setEditing(m);
                          setFormOpen(true);
                        }}
                        disabled={!canCreate}
                        aria-label={t("pages.llmModels.aria.edit")}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {m.is_active ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-danger-700 hover:text-danger-700"
                          onClick={() => handleDeactivate(m)}
                          disabled={!canCreate || deactivate.isPending}
                          aria-label={t("pages.llmModels.aria.deactivate")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LlmFormDialog
        open={formOpen}
        onOpenChange={(v) => {
          setFormOpen(v);
          if (!v) setEditing(null);
        }}
        model={editing}
      />
    </div>
  );
}
